import { useEffect, useState, useMemo, lazy } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pie, Bar, Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import api from "../../../api";
import { FaVenusMars, FaEnvelope, FaCalendarCheck } from "react-icons/fa6";
import {
  FaPercentage,
  FaPhoneAlt,
  FaBirthdayCake,
} from "react-icons/fa";
import { motion } from "framer-motion";

import { apiRequest, apiSuccess } from "../../../redux/api/apiActions";
import {
  TrendingUp,
  TrendingDown,
  Users,
  Activity,
  Target,
  BarChart3,
  PieChart,
  ChartColumn,
  ChartSpline,
} from "lucide-react";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  usePermissions,
  useUserRoles,
} from "../../../authentication/authentication";
import UnauthorizedUserPermissions from "../../UnauthorizedUserPermissions";
import { arClass } from "../../../utils/useArClass";
import textTranslator from "../../../utils/useTextTranslator";
import DateInput from "../../form/DateInput";
import CustomSelect from "../../CustomSelect";
import { isAdminRole } from "../../../utils/adminChecker";

const Txt = lazy(() => import("../../Text"));



/**
 * Text component for the Dashboard page for translations
 * @param children - The text content to be translated
 * @param className - Optional CSS class names
 * @return Translated text element
 */
const Text = ({ children, className }) => {
  return (
    <Txt page="STATISTICS" className={className} keyName="STATISTICS">
      {children}
    </Txt>
  );
};

const StatCard = ({
  icon,
  title,
  value,
  subtitle,
  trend,
  darkMode,
  color = "blue",
  defaultLanguage,
}) => {
  const colorVariants = {
    blue: {
      bg: "bg-blue-500",
      text: "text-blue-500",
      light: "bg-blue-50",
      dark: "bg-blue-900/30",
    },
    green: {
      bg: "bg-green-500",
      text: "text-green-500",
      light: "bg-green-50",
      dark: "bg-green-900/30",
    },
    purple: {
      bg: "bg-purple-500",
      text: "text-purple-500",
      light: "bg-purple-50",
      dark: "bg-purple-900/30",
    },
    amber: {
      bg: "bg-amber-500",
      text: "text-amber-500",
      light: "bg-amber-50",
      dark: "bg-amber-900/30",
    },
    rose: {
      bg: "bg-rose-500",
      text: "text-rose-500",
      light: "bg-rose-50",
      dark: "bg-rose-900/30",
    },
    indigo: {
      bg: "bg-indigo-500",
      text: "text-indigo-500",
      light: "bg-indigo-50",
      dark: "bg-indigo-900/30",
    },
  };

  let colors = colorVariants[color];

  if (trend) {
    colors = trend.value > 0 ? colorVariants.green : colorVariants.rose;
  }

  function getBarPercentages(targetValue, overtarget) {
    if (!targetValue && !overtarget) {
      return { bar1: 0, bar2: 0 };
    }

    // If no overflow → simple
    if (overtarget <= 0) {
      return {
        bar1: targetValue > 100 ? 100 : targetValue, // cap at 100 if needed
        bar2: 0,
      };
    }

    // Overflow → scale so total is 100%
    const total = parseFloat(Number(targetValue + overtarget).toFixed(2));

    return {
      bar1: parseFloat((targetValue / total) * 100).toFixed(2),
      bar2: parseFloat((overtarget / total) * 100).toFixed(2),
    };
  }

  // ensure value is a number
  const numericValue = Number(value) || 0;
  const targetValue = trend && trend.value ? Number(trend.value) : null;
  const overtarget = trend && trend.value > 100 ? trend.value - 100 : 0;
  const targetFix = targetValue - overtarget;
  const undertarget = trend && trend.value < 0 ? Math.abs(trend.value) : 0;
  const overtargetColor = darkMode ? "bg-green-700/50" : "bg-green-200";
  const overtargetTextColor = darkMode ? "text-green-700/50" : "text-green-200";

  const { bar1, bar2 } = getBarPercentages(targetFix, overtarget);

  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.02 }}
      className={arClass(
        `p-5 rounded-lg border transition-all duration-300 ${
          darkMode
            ? `bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700/50 ${colors.dark}`
            : `bg-gradient-to-br from-white to-gray-50/50 border-gray-200 ${colors.light}`
        } shadow-sm hover:shadow-sm`
      )}
    >
      <div className={arClass("flex items-start justify-between mb-4")}>
        <div
          className={arClass(
            `p-3 rounded-xl ${
              darkMode ? "bg-gray-800/60" : "bg-white"
            } shadow-sm`
          )}
        >
          <div className={arClass(`${colors.text} text-xl`)}>{icon}</div>
        </div>
        {trend && (
          <div
            className={arClass(
              `flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                trend.value > 0
                  ? darkMode
                    ? "bg-green-900/30 text-green-400"
                    : "bg-green-50 text-green-600"
                  : darkMode
                  ? "bg-rose-900/30 text-rose-400"
                  : "bg-rose-50 text-rose-600"
              }`
            )}
          >
            {trend.value > 0 ? (
              <TrendingUp size={12} />
            ) : (
              <TrendingDown size={12} />
            )}
            <span>{parseFloat(Math.abs(trend.value)).toFixed(2)}%</span>
          </div>
        )}
      </div>

      <div className={arClass("space-y-2")}>
        <p
          className={arClass(
            `text-sm font-medium ${
              darkMode ? "text-gray-400" : "text-gray-600"
            }`
          )}
        >
          {title}
        </p>
        <div className={arClass("flex items-end gap-2")}>
          <h3
            className={arClass(
              `text-2xl font-bold max-sm:text-lg ${
                darkMode ? "text-white" : "text-gray-900"
              }`
            )}
          >
            {numericValue.toLocaleString()}
          </h3>
          {subtitle && (
            <span
              className={arClass(
                `text-sm max-sm:text-xs ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                } mb-1`
              )}
            >
              {subtitle}
            </span>
          )}
        </div>
      </div>
      <div className={arClass("w-full flex justify-between relative")}>
        {targetValue !== null && targetValue >= 0 && (
          <small
            className={arClass(`flex  ${colors.text}`)}
            style={{ width: `${Math.min(bar1, 100)}%` }}
          >
            {parseFloat(targetFix).toFixed(2)}%
          </small>
        )}
        {overtarget > 0 && (
          <small
            className={`flex  ${overtargetTextColor} ${
              defaultLanguage === "ar" ? "" : "justify-end"
            }`}
            style={{ width: `${Math.min(bar2, 100)}%` }}
          >
            +{parseFloat(overtarget).toFixed(2)}%
          </small>
        )}
      </div>

      <div
        className={arClass(
          ` h-2 w-full rounded-full ${
            darkMode ? "bg-gray-700/50" : "bg-gray-200/50"
          } ${!trend || undertarget ? "hidden" : ""} overflow-hidden`
        )}
      >
        {trend?.value && (
          <div
            className={arClass(
              "flex h-full w-full rounded-full overflow-hidden"
            )}
          >
            <motion.button
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(bar1, 100)}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className={arClass(`h-full  ${colors.bg}`)}
            />
            <motion.button
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(bar2, 100)}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className={arClass(`h-full ${overtargetColor} `)}
            />
          </div>
        )}

        {/* {
          undertarget > 0 && (
            <motion.div
              initial={{ width: 100 }}
              animate={{ width: `${Math.min(undertarget, 100)}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className={arClass(`h-full rounded-full ${undertargetColor} `)}
            />
          )
        } */}
      </div>
    </motion.div>
  );
};

const ChartCard = ({
  title,
  description,
  icon,
  darkMode,
  children,
  height = "h-72",
  additionalHeaderContent,
  rightIcon,
}) => (
  <motion.div
    whileHover={{ y: -6 }}
    className={arClass(
      `p-6 rounded-lg border transition-all duration-300 ${
        darkMode
          ? "bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700/50"
          : "bg-gradient-to-br from-white to-gray-50/50 border-gray-200"
      } shadow-sm hover:shadow-md`
    )}
  >
    <div
      className={arClass(
        "flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4"
      )}
    >
      <div className={arClass("flex items-center gap-3")}>
        <div
          className={arClass(
            `p-2.5 rounded-xl ${
              darkMode
                ? "bg-gray-800/60 text-blue-400"
                : "bg-blue-50 text-blue-600"
            }`
          )}
        >
          {icon}
        </div>
        <div>
          <h3
            className={arClass(
              `text-lg font-bold max-sm:text-sm ${
                darkMode ? "text-white" : "text-gray-900"
              }`
            )}
          >
            {title}
          </h3>
          {description && (
            <p
              className={arClass(
                `text-sm max-sm:text-xs ${
                  darkMode ? "text-gray-100" : "text-gray-500"
                } mt-1`
              )}
            >
              {description}
            </p>
          )}
        </div>
      </div>

      <div className={arClass("flex items-center gap-3")}>
        {additionalHeaderContent && (
          <div className="flex items-center gap-2">
            {additionalHeaderContent}
          </div>
        )}
        {rightIcon && (
          <div
            className={arClass(
              `p-2 rounded-lg ${darkMode ? "bg-gray-800/60" : "bg-gray-100"}`
            )}
          >
            <BarChart3
              size={18}
              className={darkMode ? "text-gray-400" : "text-gray-600"}
            />
          </div>
        )}
      </div>
    </div>

    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={arClass(`${height} relative`)}
    >
      {children}
    </motion.div>
  </motion.div>
);

const DistributionBadge = ({ label, value, percentage, color, darkMode }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className={arClass(
      `flex items-center justify-between p-2 rounded-xl border min-w-[150px] max-sm:min-w-[100px] gap-2 ${
        darkMode ? "bg-gray-800/40 border-gray-700" : "bg-white border-gray-200"
      } shadow-sm`
    )}
  >
    <div className={arClass("flex items-center gap-1 max-sm:flex")}>
      <div
        className={arClass(`w-3 h-3 rounded-full`)}
        style={{ backgroundColor: color }}
      />
      <span
        className={arClass(
          `font-medium max-sm:text-xs  ${
            darkMode ? "text-gray-300" : "text-gray-700"
          }`
        )}
      >
        {label}
      </span>
     
    </div>
    <div className="flex items-center gap-1">
      <span
        className={arClass(
          `font-bold max-sm:text-xs ${darkMode ? "text-white" : "text-gray-900"}`
        )}
      >
        {value}
      </span>
      <span
        className={arClass(
          `text-sm px-2 py-1 rounded-full max-sm:text-xs ${
            darkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
          }`
        )}
      >
        {percentage}
      </span>
    </div>
  </motion.div>
);

const transformRegistrationData = (rawData = {}, mode) => {
  const monthsShort = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthsLong = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // ensure plain object
  const buckets = rawData || {};

  if (mode === "month") {
    // Day keys can be: "1","01","01/12","1st" etc. Try to pick numeric days.
    const dayMap = {};
    Object.entries(buckets).forEach(([k, v]) => {
      const n = parseInt(String(k).replace(/^0+/, ""), 10);
      if (!Number.isNaN(n) && n > 0) dayMap[n] = Number(v || 0);
    });

    // Determine days to render: use max found or fallback to 31
    const maxDay = Math.max(...Object.keys(dayMap).map(Number), 31);
    return Array.from({ length: maxDay }, (_, i) => {
      const day = i + 1;
      return { day, registrations: Number(dayMap[day] || 0) };
    });
  }

  if (mode === "year") {
    // Try numeric keys (1..12), short month names, or full month names.
    return monthsShort.map((short, idx) => {
      const long = monthsLong[idx];
      const num = String(idx + 1);
      const numPadded = String(idx + 1).padStart(2, "0");

      const rawVal =
        // exact short name
        (buckets[short] !== undefined && buckets[short]) ||
        // full month name
        (buckets[long] !== undefined && buckets[long]) ||
        // numeric keys
        (buckets[num] !== undefined && buckets[num]) ||
        (buckets[numPadded] !== undefined && buckets[numPadded]) ||
        // some APIs may expose index-based arrays
        (buckets[idx + 1] !== undefined && buckets[idx + 1]) ||
        0;

      return {
        month: textTranslator(short, "STATISTICS"),
        registrations: Number(rawVal || 0),
      };
    });
  }

  // default / other modes: preserve object order
  return Object.entries(buckets).map(([k, v]) => ({
    period: k,
    registrations: Number(v || 0),
  }));
};

// Theme based on darkMode
const getChartTheme = (darkMode) => ({
  backgroundColor: darkMode ? "#1f2937" : "#ffffff",
  gridStroke: darkMode ? "#374151" : "#e5e7eb",
  axisTick: darkMode ? "#9ca3af" : "#6b7280",
  lineStroke: darkMode ? "#3b82f6" : "#2563eb",
  tooltip: {
    backgroundColor: darkMode ? "#374151" : "#ffffff",
    border: darkMode ? "1px solid #4b5563" : "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "12px",
    color: darkMode ? "#f3f4f6" : "#111827",
  },
});

// In your component
const RegistrationChart = ({
  monthlyRegistrationsData,
  mode,
  darkMode,
  defaultLanguage,
}) => {
  const theme = getChartTheme(darkMode);
  const chartData = transformRegistrationData(monthlyRegistrationsData, mode);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={chartData}
        margin={{ top: 5, right: 8, left: 8, bottom: 5 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={theme.gridStroke}
          vertical={false}
        />

        {mode === "month" ? (
          <XAxis
            dataKey="day"
            axisLine={true}
            tickLine={false}
            tick={{
              fontSize: 11,
              fill: theme.axisTick,
            }}
            reversed={defaultLanguage === "ar"}
            label={{
              value: "Day",
              position: "insideBottom",
              offset: -5,
              fill: theme.axisTick,
              fontSize: 12,
            }}
          />
        ) : mode === "year" ? (
          <XAxis
            dataKey="month"
            axisLine={true}
            tickLine={false}
            tick={{
              fontSize: 11,
              fill: theme.axisTick,
              angle: -45,
              textAnchor: "end",
            }}
            reversed={defaultLanguage === "ar"}
          />
        ) : (
          <XAxis
            dataKey="period"
            axisLine={true}
            tickLine={false}
            tick={{
              fontSize: 11,
              fill: theme.axisTick,
            }}
            reversed={defaultLanguage === "ar"}
          />
        )}

        <YAxis
          axisLine
          tickLine={false}
          orientation={defaultLanguage === "ar" ? "right" : "left"}
          tick={{
            fontSize: 11,
            fill: theme.axisTick,
            textAnchor: defaultLanguage === "ar" ? "start" : "end",
          }}
          label={
            defaultLanguage === "ar"
              ? {
                  value: "التسجيلات",
                  angle: 90,
                  position: "insideRight", // ✅ IMPORTANT
                  dx: 8, // ✅ push label inward
                  fill: theme.axisTick,
                  fontSize: 11,
                }
              : defaultLanguage === "fr"
              ? {
                  value: "Inscriptions",
                  angle: -90,
                  position: "insideLeft", // ✅ IMPORTANT
                  dx: -8, // ✅ push label inward
                  fill: theme.axisTick,
                  fontSize: 11,
                }
              : {
                  value: "Registrations",
                  angle: -90,
                  position: "insideLeft", // ✅ IMPORTANT
                  dx: -8, // ✅ push label inward
                  fill: theme.axisTick,
                  fontSize: 11,
                }
          }
        />

        <Tooltip
          contentStyle={{
            backgroundColor: theme.tooltip.backgroundColor,
            border: theme.tooltip.border,
            borderRadius: theme.tooltip.borderRadius,
            fontSize: theme.tooltip.fontSize,
            color: theme.tooltip.color,
          }}
          formatter={
            defaultLanguage === "en"
              ? (value) => [`${value} registrations`, "Count"]
              : defaultLanguage === "fr"
              ? (value) => [`${value} inscriptions`, "Compte"]
              : (value) => [`${value} تسجيلات`, "العدد"]
          }
          labelFormatter={
            defaultLanguage === "en"
              ? (label) => {
                  if (mode === "month") return `Day ${label}`;
                  if (mode === "year") return label;
                  return `Period: ${label}`;
                }
              : defaultLanguage === "fr"
              ? (label) => {
                  if (mode === "month") return `Jour ${label}`;
                  if (mode === "year") return label;
                  return `Période : ${label}`;
                }
              : (label) => {
                  if (mode === "month") return `اليوم ${label}`;
                  if (mode === "year") return label;
                  return `الفترة: ${label}`;
                }
          }
        />

        <Legend
          verticalAlign="top"
          height={36}
          iconType="circle"
          iconSize={8}
        />

        {/* Reference lines for goals or averages if needed */}
        <Line
          type="monotone"
          dataKey="registrations"
          name={textTranslator("Registrations", "STATISTICS")}
          stroke={theme.lineStroke}
          strokeWidth={2}
          dot={{ r: 4, fill: theme.lineStroke, strokeWidth: 2 }}
          activeDot={{ r: 6, fill: theme.lineStroke }}
        />

        {/* Optional: Add average line */}
        {/* <ReferenceLine
          y={averageRegistrations}
          stroke="#94a3b8"
          strokeDasharray="3 3"
          opacity={0.7}
          label={{
            value: 'Avg',
            position: 'right',
            fill: '#94a3b8',
            fontSize: 10
          }}
        /> */}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default function PatientStatistics() {
  const dispatch = useDispatch();
  const darkMode = useSelector((state) => state.settings.darkMode);

  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage
  );
  const [chartType, setChartType] = useState("bar"); // 'bar' | 'doughnut'
  const [stats, setStats] = useState({
    count: 0,
    // normalize gender to lowercase keys for UI but keep originals available
    gender: { female: 0, male: 0, other: 0, Female: 0, Male: 0 },
    ageGroups: { "0-20": 0, "21-40": 0, "41-60": 0, "61+": 0 },
    civilStatus: {
      Single: 0,
      Married: 0,
      Divorced: 0,
      Widowed: 0,
      Child: 0,
      Other: 0,
    },
    contactMethods: { email: 0, phone: 0, both: 0, none: 0 },
    monthlyRegistrations: {},
    monthlyRegistrationsPercent: {},
    registrations: {
      totalInPeriod: 0,
      totalAllTime: 0,
      percentOfAllTime: 0,
      previousTotal: 0,
      changeAbsolute: 0,
      changePercent: 0,
      trend: "",
    },
    period: { mode: "month", start: null, end: null },
    generatedAt: null,
    weeklyTrend: 0,
    monthlyTrend: 0,
  });

  const [loading, setLoading] = useState(true);
  // mode controls: 'day' | 'month' | 'year'
  const [mode, setMode] = useState("month");
  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1); // 1-12
  const [selectedDate, setSelectedDate] = useState(
    today.toISOString().split("T")[0]
  );

  const patientStatisticsKey = "patientStatistics";
  const { requests } = useSelector((state) => state.api);
  const patientStatisticsLoading =
    requests[patientStatisticsKey]?.loading || loading;

  const { permissions, loadingPermissions } = usePermissions();
  const roles = useUserRoles();

  useEffect(() => {
    if (mode !== "day") {
      // Keep selectedDate sync with year + month
      const day = selectedDate ? Number(selectedDate.split("-")[2]) : 1;

      const newDate = `${selectedYear
        .toString()
        .padStart(4, "0")}-${selectedMonth.toString().padStart(2, "0")}-${day
        .toString()
        .padStart(2, "0")}`;

      setSelectedDate(newDate);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    if (!isAdminRole(roles) && !permissions.patientsPermissions.get) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();

    const fetchStats = async () => {
      setLoading(true);
      dispatch(apiRequest("GET", patientStatisticsKey));

      try {
        // Build query params expected by backend:
        // mode=year/month/day and year/month/date when required
        const params = {};
        if (mode) params.mode = mode;
        if (mode === "year") params.year = Number(selectedYear);
        if (mode === "month") {
          params.year = Number(selectedYear);
          params.month = Number(selectedMonth);
        }
        if (mode === "day") {
          params.date = String(selectedDate); // YYYY-MM-DD
        }

        const res = await api.get("/statistics/demographics", {
          signal: controller.signal,
          params,
        });

        const data = res.data?.data || res.data;

        if (data) {
          // normalize incoming data to expected local shape
          const incomingGender = data.gender || {};
          const normGender = {
            female: Number(incomingGender.Female ?? incomingGender.female ?? 0),
            male: Number(incomingGender.Male ?? incomingGender.male ?? 0),
            other: Number(incomingGender.Other ?? incomingGender.other ?? 0),
            Female: Number(incomingGender.Female ?? incomingGender.female ?? 0),
            Male: Number(incomingGender.Male ?? incomingGender.male ?? 0),
            Other: Number(incomingGender.Other ?? incomingGender.other ?? 0),
          };

          const incomingAge = data.ageGroups || {};
          const ageGroups = {
            "0-20": Number(incomingAge["0-20"] ?? 0),
            "21-40": Number(incomingAge["21-40"] ?? 0),
            "41-60": Number(incomingAge["41-60"] ?? 0),
            "61+": Number(incomingAge["61+"] ?? 0),
          };

          // civil status: keep keys as provided but ensure common keys exist
          const incomingCivil = data.civilStatus || {};
          const civilStatus = {
            Single: Number(incomingCivil.Single ?? 0),
            Married: Number(incomingCivil.Married ?? 0),
            Divorced: Number(incomingCivil.Divorced ?? 0),
            Widowed: Number(incomingCivil.Widowed ?? 0),
            Child: Number(incomingCivil.Child ?? 0),
            Other: Number(incomingCivil.Other ?? incomingCivil.other ?? 0),
            ...incomingCivil, // keep any additional keys
          };

          const contactMethods = {
            email: Number(data.contactMethods?.email ?? 0),
            phone: Number(data.contactMethods?.phone ?? 0),
            both: Number(data.contactMethods?.both ?? 0),
            none: Number(data.contactMethods?.none ?? 0),
          };

          const monthlyRegistrations = data.monthlyRegistrations || {};
          const monthlyRegistrationsPercent =
            data.monthlyRegistrationsPercent || {};

          setStats({
            count: Number(data.count || 0),
            gender: normGender,
            ageGroups,
            civilStatus,
            contactMethods,
            monthlyRegistrations,
            monthlyRegistrationsPercent,
            registrations: data.registrations || {
              totalInPeriod: 0,
              totalAllTime: 0,
              percentOfAllTime: "0%",
            },
            period: data.period || { mode, start: null, end: null },
            generatedAt: data.generatedAt || null,
            weeklyTrend: Number(data.weeklyTrend ?? 0),
            monthlyTrend: Number(data.monthlyTrend ?? 0),
          });
        }

        dispatch(apiSuccess("GET", patientStatisticsKey));
      } catch (error) {
        if (error.name !== "AbortError") {
          // Handle error silently
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    return () => controller.abort();
  }, [mode, selectedYear, selectedMonth, selectedDate, dispatch]);

  // Chart data calculations
  const chartColors = useMemo(
    () => [
      "#8B5CF6",
      "#10B981",
      "#F59E0B",
      "#EF4444",
      "#3B82F6",
      "#EC4899",
      "#14B8A6",
      "#F97316",
      "#6366F1",
      "#84CC16",
    ],
    []
  );

  const gradientColors = useMemo(
    () => [
      ["#8B5CF6", "#7C3AED"],
      ["#10B981", "#059669"],
      ["#F59E0B", "#D97706"],
      ["#EF4444", "#DC2626"],
      ["#3B82F6", "#2563EB"],
    ],
    []
  );

  const createGradient = (ctx, color1, color2) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, color1);
    gradient.addColorStop(1, color2);
    return gradient;
  };

  const genderChartData = useMemo(() => {
    const ctx = document.createElement("canvas").getContext("2d");
    return {
      labels: ["Female", "Male"],
      datasets: [
        {
          data: [stats.gender.female, stats.gender.male],
          backgroundColor: [
            createGradient(ctx, gradientColors[0][0], gradientColors[0][1]),
            createGradient(ctx, gradientColors[1][0], gradientColors[1][1]),
            createGradient(ctx, gradientColors[2][0], gradientColors[2][1]),
          ],
          borderWidth: 2,
          borderColor: darkMode ? "rgb(31, 41, 55)" : "white",
          hoverBorderWidth: 3,
          hoverBorderColor: darkMode ? "rgb(55, 65, 81)" : "rgb(243, 244, 246)",
          borderRadius: 12,
          spacing: 6,
        },
      ],
    };
  }, [stats.gender, darkMode, gradientColors, ]);

  // Translation configuration
  const translations = {
    en: {
      female: "Female",
      male: "Male",
      persons: "persons",
      total: "Total",
    },
    ar: {
      female: "أنثى",
      male: "ذكر",
      persons: "أشخاص",
      total: "الإجمالي",
    },
    fr: {
      female: "Femme",
      male: "Homme",
      persons: "personnes",
      total: "Total",
    },
  };

  const ageChartData = useMemo(() => {
    const AGE_KEYS = ["0-20", "21-40", "41-60", "61+"];

    const ageGroupTranslations = {
      en: {
        "0-20": "0–20",
        "21-40": "21–40",
        "41-60": "41–60",
        "61+": "61+",
      },
      fr: {
        "0-20": "0–20 ans",
        "21-40": "21–40 ans",
        "41-60": "41–60 ans",
        "61+": "61 ans et plus",
      },
      ar: {
        "0-20": "0–20 سنة",
        "21-40": "21–40 سنة",
        "41-60": "41–60 سنة",
        "61+": "61 سنة فما فوق",
      },
    };

    const ctx = document.createElement("canvas").getContext("2d");

    const labels = AGE_KEYS.map(
      (key) => ageGroupTranslations[defaultLanguage]?.[key] || key
    );

    const data = AGE_KEYS.map((key) => stats.ageGroups?.[key] || 0);
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: gradientColors
            .slice(0, AGE_KEYS.length)
            .map(([c1, c2]) => createGradient(ctx, c1, c2)),
          borderWidth: 2,
          borderColor: darkMode ? "rgb(31, 41, 55)" : "white",
          borderRadius: 12,
        },
      ],
    };
  }, [stats.ageGroups, darkMode, gradientColors, defaultLanguage]);

  const monthlyRegistrationsData = useMemo(() => {
    const ctx = document.createElement("canvas").getContext("2d");
    // Use backend-provided buckets order (object insertion order) when available.
    const buckets = stats.monthlyRegistrations || {};
    const labels = Object.keys(buckets);
    const data = labels.map((k) => buckets[k] || 0);

    // fallback label set when empty (last 12 months)
    const fallbackMonths = [
      textTranslator("Jan", "STATISTICS"),
      textTranslator("Feb", "STATISTICS"),
      textTranslator("Mar", "STATISTICS"),
      textTranslator("Apr", "STATISTICS"),
      textTranslator("May", "STATISTICS"),
      textTranslator("Jun", "STATISTICS"),
      textTranslator("Jul", "STATISTICS"),
      textTranslator("Aug", "STATISTICS"),
      textTranslator("Sep", "STATISTICS"),
      textTranslator("Oct", "STATISTICS"),
      textTranslator("Nov", "STATISTICS"),
      textTranslator("Dec", "STATISTICS"),
    ];

    let finalLabels = labels.length ? labels : fallbackMonths;
    if (mode === "year") {
      finalLabels = fallbackMonths;
    }

    return {
      labels: finalLabels,
      datasets: [
        {
          label: "Registrations",
          data: labels.length
            ? data
            : fallbackMonths.map((m) => stats.monthlyRegistrations?.[m] || 0),
          backgroundColor: createGradient(ctx, "#8B5CF6", "#7C3AED"),
          borderColor: "#8B5CF6",
          borderWidth: 2,
          borderRadius: 8,
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [stats.monthlyRegistrations, darkMode, ]);

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: "right",
        labels: {
          color: darkMode ? "#E5E7EB" : "#4B5563",
          font: { family: "Inter, sans-serif", size: 12 },
          padding: 20,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        backgroundColor: darkMode
          ? "rgba(17, 24, 39, 0.95)"
          : "rgba(255, 255, 255, 0.95)",
        titleColor: darkMode ? "#F3F4F6" : "#111827",
        bodyColor: darkMode ? "#D1D5DB" : "#374151",
        borderColor: darkMode ? "#374151" : "#E5E7EB",
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: (context) => {
            const label = context.label || "";
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage =
              total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    animation: {
      animateScale: true,
      animateRotate: true,
      duration: 1000,
      easing: "easeOutQuart",
    },
    elements: {
      arc: {
        borderWidth: 2,
        borderColor: darkMode ? "rgb(31, 41, 55)" : "white",
      },
    },
  };

  const barChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: darkMode
            ? "rgba(55, 65, 81, 0.3)"
            : "rgba(229, 231, 235, 0.5)",
          drawBorder: false,
        },
        ticks: {
          color: darkMode ? "#9CA3AF" : "#6B7280",
          font: { family: "Inter, sans-serif" },
          align: defaultLanguage === "ar" ? "end" : "start",
        },
        position: defaultLanguage === "ar" ? "right" : "left",
      },
      x: {
        grid: { display: false },
        ticks: {
          color: darkMode ? "#9CA3AF" : "#6B7280",
          font: { family: "Inter, sans-serif" },
        },
        reverse: defaultLanguage === "ar",
      },
    },
    plugins: {
      ...chartOptions.plugins,
      legend: { display: false },
    },
  };

  if (patientStatisticsLoading || (!isAdminRole(roles) && loadingPermissions)) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={arClass(
          `flex items-center justify-center p-8 rounded-lg h-[600px] ${
            darkMode
              ? "bg-gradient-to-br from-gray-800 to-gray-900 "
              : "bg-gradient-to-br from-gray-50 to-white"
          }`
        )}
      >
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
            className={arClass(
              `w-16 h-16 border-4 rounded-full border-t-transparent mx-auto ${
                darkMode ? "border-blue-500" : "border-blue-600"
              }`
            )}
          />
          <p
            className={arClass(
              `mt-4 ${darkMode ? "text-gray-400" : "text-gray-600"}`
            )}
          >
            <Text>Loading patient statistics...</Text>
          </p>
        </div>
      </motion.div>
    );
  }

  if (!isAdminRole(roles) && !permissions.patientsPermissions.get) {
    return (
      <UnauthorizedUserPermissions
        permissionsKey="patientsPermissions"
        method="get"
      />
    );
  }

  const currentYear = today.getFullYear();
  let years = Array.from({ length: 6 }).map((_, i) => currentYear - i);
  let yearsOptions = () => years.map((y) => ({ value: y, label: y.toString() }));
  let monthsOptions = () =>
    Array.from({ length: 12 }).map((_, i) => {
      const m = i + 1;
      return { value: m, label: new Date(0, i).toLocaleString(defaultLanguage, { month: "long" }) };
    });

  // Ensure selectedYear is included
  if (!years.includes(selectedYear)) {
    years = [selectedYear, ...years].sort((a, b) => b - a); // sort descending
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={arClass(
        `relative p-4 sm:p-6 rounded-lg ${
          darkMode
            ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
            : "bg-gradient-to-br from-gray-50 via-white to-gray-50"
        }  border ${darkMode ? "border-gray-700" : "border-gray-200"}`
      )}
    >
      {/* Header */}
      <div
        className={arClass(
          "flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8"
        )}
      >
        <div className={arClass("flex items-center gap-4")}>
          <div
            className={arClass(
              `p-3 rounded-xl ${
                darkMode
                  ? "bg-gradient-to-br from-blue-900/30 to-indigo-900/30 text-blue-400"
                  : "bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600"
              }`
            )}
          >
            <PieChart size={24} />
          </div>
          <div>
            <h1
              className={arClass(
                `text-2xl font-bold max-sm:text-lg ${
                  darkMode ? "text-white" : "text-gray-900"
                }`
              )}
            >
              <Text>Patient Statistics</Text>
            </h1>
            <p
              className={arClass(
                `text-sm ${darkMode ? "text-gray-400" : "text-gray-500"} mt-1`
              )}
            >
              <Text>
                Comprehensive overview of patient demographics and trends
              </Text>
            </p>
          </div>
        </div>

        <div
          className={arClass(
            "flex items-center gap-3 max-sm:flex-col max-sm:items-stretch max-sm:w-full"
          )}
        >
          <div
            className={arClass(
              `flex items-center gap-2 max-sm:w-full max-sm:justify-between`
            )}
          >
            {["day", "month", "year"].map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={arClass(
                  `p-3 text-sm rounded-md font-medium transition-colors border w-full sm:w-auto max-sm:text-xs max-sm:p-2 ${
                    mode === m
                      ? "bg-blue-600 text-white"
                      : darkMode
                      ? "bg-gray-700 border-gray-300 text-white hover:bg-gray-900"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`
                )}
              >
                <Text>{m.charAt(0).toUpperCase() + m.slice(1)}</Text>
              </button>
            ))}
          </div>

          {/* Dynamic selectors based on mode */}
          <div
            className={arClass(
              `flex items-center gap-2 max-sm:w-full max-sm:justify-between`
            )}
          >
            {mode === "year" && (
              <CustomSelect
                    options={yearsOptions()}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    allowCreate={false}
                    allowSearch={false}
                    darkMode={darkMode}
                    className="min-w-[150px] max-sm:min-w-max"
                  />
            
            )}

            {mode === "month" && (
              <>
                <CustomSelect
                    options={yearsOptions()}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    allowCreate={false}
                    allowSearch={false}
                    darkMode={darkMode}
                    className="min-w-[150px] max-sm:min-w-max"
                  />

                <CustomSelect
                  options={monthsOptions()}
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  allowCreate={false}
                  allowSearch={false}
                  darkMode={darkMode}
                  className="min-w-[150px] max-sm:min-w-max"
                />
              </>
            )}

            {mode === "day" && (
              <DateInput
                value={selectedDate}
                onChange={(e) => {
                  if (
                    selectedDate !== e.target.value &&
                    e.target.value !== ""
                  ) {
                    setSelectedDate(e.target.value);
                    const parts = e.target.value.split("-");
                    if (parts.length === 3) {
                      setSelectedYear(Number(parts[0]));
                      setSelectedMonth(Number(parts[1]));
                    }
                  }
                }}
                name="date-input" // Add a name for form handling
                className={`p-2 rounded-md border text-sm w-full max-sm:text-xs min-w-[300px] max-sm:min-w-max ${
                  darkMode
                    ? "bg-gray-700 text-white border-gray-300 hover:bg-gray-900"
                    : "bg-white text-gray-700 border-gray-300"
                }`}
                placeholder={
                  defaultLanguage === "en"
                    ? "Select date"
                    : defaultLanguage === "ar"
                    ? "اختر تاريخًا"
                    : "Sélectionner une date"
                }
                // Optional: Add minDate and maxDate constraints
                // minDate="2024-01-01"
                // maxDate="2024-12-31"
                format="YYYY-MM-DD" // Match your current date format
                showTodayButton={true}
                allowClear={true}
                // If you're using React Hook Form, pass these props:
                // register={register}
                // registerProp="dateFieldName"
              />
            )}
          </div>
        </div>
      </div>

      {stats.count === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={arClass(
            `p-12 rounded-xl text-center ${
              darkMode ? "bg-gray-800/50" : "bg-gray-50"
            }`
          )}
        >
          <div
            className={arClass(
              `w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${
                darkMode
                  ? "bg-gray-800 text-gray-400"
                  : "bg-gray-200 text-gray-500"
              }`
            )}
          >
            <Users size={32} />
          </div>
          <h3
            className={arClass(
              `text-xl font-semibold mb-2 max-sm:text-lg ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`
            )}
          >
            <Text>No Patient Data Available</Text>
          </h3>
          <p
            className={arClass(
              `mb-6 max-sm:text-xs ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`
            )}
          >
            <Text>Add patients to start seeing analytics and insights</Text>
          </p>
          <button
            className={arClass(
              `px-6 py-2.5 rounded-lg font-medium max-sm:text-xs ${
                darkMode
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              } transition-colors`
            )}
          >
            <Text>Add First Patient</Text>
          </button>
        </motion.div>
      ) : (
        <>
          {/* Stats Grid */}
          <div
            className={arClass(
              "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
            )}
          >
            <StatCard
              icon={<Users size={20} />}
              title={<Text>Total Patients</Text>}
              value={stats.registrations.totalAllTime}
              subtitle={<Text>All-time total</Text>}
              trend={{ value: stats.registrations.percentOfAllTime }}
              darkMode={darkMode}
              color="blue"
              target={stats.registrations.totalAllTime}
              defaultLanguage={defaultLanguage}
            />

            <StatCard
              icon={<Activity size={20} />}
              title={
                mode === "day" ? (
                  <Text>New Registrations (Daily)</Text>
                ) : mode === "month" ? (
                  <Text>New Registrations (Monthly)</Text>
                ) : (
                  <Text>New Registrations (Yearly)</Text>
                )
              }
              value={Object.values(stats.monthlyRegistrations).reduce(
                (a, b) => a + b,
                0
              )}
              subtitle={<Text>New registrations</Text>}
              trend={{ value: stats.registrations.changePercent }}
              darkMode={darkMode}
              color="green"
              target={stats.registrations.previousTotal}
              defaultLanguage={defaultLanguage}
            />

            <StatCard
              icon={<Target size={20} />}
              title={
                mode === "day" ? (
                  <Text>Female Patients (Daily)</Text>
                ) : mode === "month" ? (
                  <Text>Female Patients (Monthly)</Text>
                ) : (
                  <Text>Female Patients (Yearly)</Text>
                )
              }
              value={stats.gender.female}
              subtitle={
                <>
                  <span>
                    {((stats.gender.female / stats.count) * 100).toFixed(1)}%
                  </span>
                  <span className="ml-1">
                    {" "}
                    <Text>of total</Text>
                  </span>
                </>
              }
              darkMode={darkMode}
              color="purple"
              defaultLanguage={defaultLanguage}
            />

            <StatCard
              icon={<Target size={20} />}
              title={
                mode === "day" ? (
                  <Text>Male Patients (Daily)</Text>
                ) : mode === "month" ? (
                  <Text>Male Patients (Monthly)</Text>
                ) : (
                  <Text>Male Patients (Yearly)</Text>
                )
              }
              value={stats.gender.male}
              subtitle={
                <>
                  <span>
                    {((stats.gender.male / stats.count) * 100).toFixed(1)}%
                  </span>
                  <span className="ml-1">
                    {" "}
                    <Text>of total</Text>
                  </span>
                </>
              }
              darkMode={darkMode}
              color="amber"
              defaultLanguage={defaultLanguage}
            />
          </div>

          {/* Main Charts */}
          <div
            className={arClass(
              "grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 h-[560px] max-lg:!h-[1000px] "
            )}
          >
            <ChartCard
              title={
                mode === "day" ? (
                  <Text>Gender Distribution (Daily)</Text>
                ) : mode === "month" ? (
                  <Text>Gender Distribution (Monthly)</Text>
                ) : (
                  <Text>Gender Distribution (Yearly)</Text>
                )
              }
              description={<Text>Patient demographics by gender</Text>}
              icon={<FaVenusMars size={20} />}
              darkMode={darkMode}
              height="h-[400px] "
            >
              <div className="h-full flex flex-col justify-center">
                <div className="h-full w-full">
  <Doughnut
                  data={genderChartData}
                  height={250}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      tooltip: {
                        callbacks: {
                          title: (context) => {
                            const label = context[0]?.label || "";
                            return (
                              translations[defaultLanguage]?.[
                                label.toLowerCase()
                              ] || label
                            );
                          },

                          label: (context) => {
                            const label = context.label || "";
                            const value = context.raw || 0;
                            const total = context.dataset.data.reduce(
                              (a, b) => a + b,
                              0
                            );
                            const percentage =
                              total > 0
                                ? ((value / total) * 100).toFixed(1)
                                : 0;

                            const translatedLabel =
                              translations[defaultLanguage]?.[
                                label.toLowerCase()
                              ] || label;

                            return `${translatedLabel}: ${value} (${percentage}%)`;
                          },
                        },
                      },
                      legend: {
                        position: "right",
                        labels: {
                          usePointStyle: true,
                          generateLabels: (chart) => {
                            const data = chart.data;
                            if (!data.labels) return [];

                            return data.labels.map((label, i) => {
                              const translated =
                                translations[defaultLanguage]?.[
                                  label.toLowerCase()
                                ] || label;

                              return {
                                text: translated, // ✅ translated
                                fillStyle: data.datasets[0].backgroundColor[i],
                                strokeStyle:
                                  data.datasets[0].backgroundColor[i],
                                lineWidth: 0,
                                hidden: !chart.getDataVisibility(i),
                                index: i,
                              };
                            });
                          },
                        },
                      },
                    },
                  }}
                />
</div>
                
                <div className="grid grid-cols-2 gap-3 mt-4 max-sm:grid-cols-1">
                  {genderChartData.labels.map((label, index) => {
                    const value =
                      stats.gender[label.toLowerCase()] ||
                      stats.gender[label] ||
                      0;
                    const percentage = stats.count
                      ? ((value / stats.count) * 100).toFixed(1)
                      : "0.0";

                    // Translate the label based on current language
                    const translatedLabel =
                      translations[defaultLanguage][label.toLowerCase()] ||
                      label;
                    return (
                      <DistributionBadge
                        key={label}
                        label={translatedLabel}
                        value={`${value} ${translations[defaultLanguage].persons}`}
                        percentage={`${percentage}%`}
                        color={gradientColors[index]?.[0] || chartColors[index]}
                        darkMode={darkMode}
                        // Add total count if needed
                        total={`${translations[defaultLanguage].total}: ${stats.count}`}
                      />
                    );
                  })}
                </div>
              </div>
            </ChartCard>

            <ChartCard
              title={
                mode === "day" ? (
                  <Text>Age Group Distribution (Daily)</Text>
                ) : mode === "month" ? (
                  <Text>Age Group Distribution (Monthly)</Text>
                ) : (
                  <Text>Age Group Distribution (Yearly)</Text>
                )
              }
              description={<Text>Patient distribution across age groups</Text>}
              icon={<FaBirthdayCake size={20} />}
              darkMode={darkMode}
            >
              <Pie data={ageChartData} options={chartOptions} />
            </ChartCard>
          </div>

          {/* Bottom Charts */}
          <div className={arClass("grid grid-cols-1 lg:grid-cols-2 gap-6")}>
            <ChartCard
              title={
                mode === "day" ? (
                  <>
                    <Text>Daily Registrations</Text>
                    {` (${selectedDate})`}
                  </>
                ) : mode === "month" ? (
                  <>
                    <Text>Monthly Registrations</Text>
                    {` (${selectedMonth}/${selectedYear})`}
                  </>
                ) : (
                  <>
                    <Text>Yearly Registrations</Text>
                    {` (${selectedYear})`}
                  </>
                )
              }
              description={<Text>Patient sign-ups over time</Text>}
              icon={<FaCalendarCheck size={20} />}
              darkMode={darkMode}
              height="h-[400px] max-sm:h-[300px]"
              // Add chart type toggle if needed
              additionalHeaderContent={
                <div className="flex items-center space-x-2">
                  <div
                    className={arClass(
                      "flex  rounded-lg p-1 gap-1 border border-gray-100"
                    )}
                  >
                    <button
                      onClick={() => setChartType("bar")}
                      className={arClass(
                        `p-2 text-sm rounded-md transition-colors  ${
                          darkMode
                            ? "bg-slate-600/80 hover:text-white"
                            : "bg-slate-200 hover:text-black"
                        } ${
                          chartType === "bar" && darkMode
                            ? "!bg-green-600/80 !text-green-200 border-green-200"
                            : chartType === "bar"
                            ? "!bg-green-200 !text-green-900"
                            : "text-gray-600  hover:text-gray-900 "
                        }`
                      )}
                    >
                      <ChartColumn size={20} className="inline-block ml-1" />
                    </button>
                    <button
                      onClick={() => setChartType("line")}
                      className={arClass(
                        `p-2 text-sm rounded-md transition-colors  ${
                          darkMode
                            ? "bg-slate-600/80 hover:text-white"
                            : "bg-slate-200 hover:text-black"
                        } ${
                          chartType === "line" && darkMode
                            ? "!bg-green-600/80 !text-green-200 border-green-200"
                            : chartType === "line"
                            ? "!bg-green-200 !text-green-900"
                            : "text-gray-600  hover:text-gray-900 "
                        }`
                      )}
                    >
                      <ChartSpline size={20} className="inline-block " />
                    </button>
                  </div>
                </div>
              }
            >
              {chartType === "bar" ? (
                <Bar
                  data={monthlyRegistrationsData}
                  options={barChartOptions}
                />
              ) : (
                <RegistrationChart
                  monthlyRegistrationsData={stats.monthlyRegistrations}
                  mode={mode}
                  darkMode={darkMode}
                  defaultLanguage={defaultLanguage}
                />
              )}
            </ChartCard>

            <ChartCard
              title={
                mode === "day" ? (
                  <Text>Contact Methods (Daily)</Text>
                ) : mode === "month" ? (
                  <Text>Contact Methods (Monthly)</Text>
                ) : (
                  <Text>Contact Methods (Yearly)</Text>
                )
              }
              description={<Text>Preferred contact methods</Text>}
              icon={<FaEnvelope size={20} />}
              darkMode={darkMode}
              height="h-64"
            >
              <div className={arClass("h-full flex flex-col justify-center")}>
                <div className={arClass("space-y-4")}>
                  {Object.entries(stats.contactMethods).map(
                    ([method, count], index) => {
                      const percentage = ((count / stats.count) * 100).toFixed(
                        1
                      );
                      const Icon =
                        method === "phone"
                          ? FaPhoneAlt
                          : method === "email"
                          ? FaEnvelope
                          : method === "both"
                          ? FaPhoneAlt
                          : FaPercentage;

                      return (
                        <div
                          key={method}
                          className={arClass("flex items-center gap-4")}
                        >
                          <div
                            className={arClass(
                              `p-2 rounded-lg ${
                                darkMode ? "bg-gray-800/60" : "bg-gray-100"
                              }`
                            )}
                          >
                            <Icon
                              size={16}
                              className={
                                darkMode ? "text-gray-400" : "text-gray-600"
                              }
                            />
                          </div>
                          <div className={arClass("flex-1")}>
                            <div
                              className={arClass("flex justify-between mb-1")}
                            >
                              <span
                                className={arClass(
                                  `font-medium capitalize ${
                                    darkMode ? "text-gray-300" : "text-gray-700"
                                  }`
                                )}
                              >
                                <Text>{method}</Text> ({percentage}%)
                              </span>
                              <span
                                className={`font-bold ${
                                  darkMode ? "text-white" : "text-gray-900"
                                }`}
                              >
                                {count}
                              </span>
                            </div>
                            <div
                              className={arClass(
                                `h-2 rounded-full overflow-hidden ${
                                  darkMode ? "bg-gray-700" : "bg-gray-200"
                                }`
                              )}
                            >
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${percentage}%` }}
                                transition={{ duration: 1, delay: index * 0.1 }}
                                className={arClass("h-full rounded-full")}
                                style={{
                                  backgroundColor:
                                    chartColors[index % chartColors.length],
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            </ChartCard>
          </div>

          {/* Footer Summary */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className={`mt-8 p-4 rounded-xl border ${
              darkMode
                ? "bg-gradient-to-r from-gray-800/50 to-gray-900/50 border-gray-700"
                : "bg-gradient-to-r from-blue-50/50 to-indigo-50/50 border-blue-200"
            }`}
          >
            <div
              className={arClass(
                "flex items-center justify-between max-sm:flex-col max-sm:items-start gap-4"
              )}
            >
              <div className={arClass("flex items-center gap-3")}>
                <div
                  className={`p-2 rounded-lg ${
                    darkMode
                      ? "bg-blue-900/30 text-blue-400"
                      : "bg-blue-100 text-blue-600"
                  }`}
                >
                  <Activity size={18} />
                </div>
                <div>
                  <p
                    className={arClass(
                      `text-sm font-medium ${
                        darkMode ? "text-gray-400" : "text-gray-600"
                      }`
                    )}
                  >
                    <Text>Summary</Text>
                  </p>
                  <p
                    className={arClass(
                      `text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`
                    )}
                  >
                    {stats.count} <Text>Total Patients</Text> •{" "}
                    {Object.values(stats.monthlyRegistrations).reduce(
                      (a, b) => a + b,
                      0
                    )}{" "}
                    <Text>this_month</Text>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </motion.div>
  );
}
